import React from 'react';
import styles from './Submenu.module.scss';
import { ColorCx, Text } from '@xarvio/corporate-styles';
import { BadgeWrapper, IconCircle, IconFont } from '@xarvio/elements';
import cx from 'classnames';
import { ToolTip, TooltipTypesType } from '@xarvio/tooltip';
import { Row, RowCell } from '@xarvio/layout';
import { SubmenuStateContext } from './types';

export interface SubmenuItemProps {
  className?: string;
  title?: string;
  titleEl?: React.ReactNode;
  iconName?: string;
  iconEl?: React.ReactNode;
  chevron?: 'open' | 'closed';
  badgeNumber?: number;
  badgeEl?: React.ReactNode;
  sideEl?: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
  withTooltip?: boolean;
}

const SubmenuItem: React.FC<React.PropsWithChildren<SubmenuItemProps>> = ({
  className,
  title,
  titleEl,
  iconName,
  iconEl,
  chevron,
  badgeNumber,
  badgeEl,
  sideEl,
  onClick,
  isSelected,
  withTooltip = true,
}) => {
  const { isCollapsed } = React.use(SubmenuStateContext);

  const Tag = onClick ? 'button' : 'div';
  const classNames = cx(styles.item, className, {
    [styles.item_selected]: isSelected,
  });

  const icon =
    iconEl ||
    (iconName ? (
      <IconFont name={iconName} className={styles.itemIcon20} />
    ) : null);

  // no icon, no collapsed state
  if (!icon && isCollapsed) {
    return null;
  }

  const badge = (badgeEl || badgeNumber) && (
    <>
      {badgeEl || (
        <IconCircle size={16} bg={ColorCx.GREY_L1} color={ColorCx.BLACK}>
          {badgeNumber}
        </IconCircle>
      )}
    </>
  );

  const el = (
    <Tag className={classNames} onClick={onClick}>
      {icon && (
        <div className={styles.itemIcon}>
          {badge ? (
            <BadgeWrapper
              badgeEl={badge}
              badgeStyle={{ right: 'auto', left: 12, top: -8 }}
              className={styles.itemIconBadge}
            >
              {icon}
            </BadgeWrapper>
          ) : (
            icon
          )}
        </div>
      )}
      <div className={styles.itemLabel}>
        {titleEl || <Text type={'h6'}>{title}</Text>}
      </div>
      {badge && <div className={styles.itemRight}>{badge}</div>}
      {sideEl}
      {chevron && (
        <div className={styles.itemRight}>
          <IconFont
            name={chevron === 'open' ? 'chevron-up' : 'chevron-down'}
            size={16}
            color={ColorCx.GREY}
          />
        </div>
      )}
    </Tag>
  );

  if (!withTooltip || !isCollapsed) {
    return el;
  }

  return (
    <ToolTip
      {...((title || titleEl) && {
        popupContent: (
          <Row align={'center'} minHeight={28}>
            <RowCell>{titleEl || <Text type={'h6'}>{title}</Text>}</RowCell>
          </Row>
        ),
      })}
      placement={'right'}
      className={styles.itemWrapper}
      mouseEnterDelay={0.3}
      mouseLeaveDelay={0.1}
      type={isSelected ? TooltipTypesType.BLUE : TooltipTypesType.BLACK}
    >
      {el}
    </ToolTip>
  );
};

export { SubmenuItem };
